import { FC, useEffect, useState } from 'react';

import {
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons';
import { Button, Input } from 'antd';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { ReportMenu } from './reportMenu/reportMenu';
import { MenuTab } from '../../../../../app/providers/redux/slices/app/types';
import { Tag } from '../../../../../app/providers/redux/slices/reports/params/reports/types';
import { defaultGroupings, defaultMetrics } from '../../../../../app/providers/redux/slices/reports/table/tableSlice';
import { getLinkPathMenu } from '../../../../../helpers/utils/functions';
import {
  parseTableData,
  handleReportPeriod,
} from '../../../../../helpers/utils/reports';
import { routeNames } from '../../../constants/routes';
import { useActions } from '../../../hooks/useActions';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import Loader from '../../loader/Loader';
import styles from '../styles/TabContent.module.scss';
import { DatePeriodType, MainType, TabType } from '../types';
import { filterByParams } from '../utils/filter-reports';

export type ContentTab = 'all' | 'my' | 'schools' | 'scan';

export type TabContentProps = {
  mainTab: MenuTab;
  subTab: 'all' | 'favorites';
  activeContentTab: ContentTab;
  onContentTabChange: (key: 'all' | 'my' | 'schools' | 'scan') => void;
  data: Array<{
    id: number;
    name: string;
    type: string;
    is_favorite: boolean;
    category?: string;
    tags?: any;
    values?: any;
    report_id: any;
    hash: any;
  }>;
  activeReportTab?: MenuTab | DatePeriodType;
  activeTagTab?: 'manager' | 'traffic' | 'funnels' | 'contractors';
  activeTab?: TabType;
  onItemClick?: () => void;
  tags: {
    dashboards: Tag[];
    reports: Tag[];
  };
  activeMainTab: MenuTab;
  collapsed: boolean;
};

export type ItemTypee = {
  id: any;
  name: string;
  type: string;
  access_id: number;
  is_favorite: boolean;
  user_id: number;
  category?: string;
  tags?: Array<{ id: number; name: string }>;
  values?: any;
  report_id?: any;
  hash?: any;
  static?: true;
};

const getLinkStyle = (isHover: boolean, isSelect: boolean) => ({
  fontSize: '20px',
  padding: '10px 20px ',
  paddingBottom: '10px',
  cursor: 'pointer',
  marginBottom: '10px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxShadow: isHover ? '0px 2px 5px -5px rgba(34, 60, 80, 0.6)' : 'none',
  background: isSelect ? '#d6d6d6' : isHover ? '#f5f5f5' : 'none',
  borderTopLeftRadius: '30px',
  borderBottomLeftRadius: '30px',
  minWidth: '500px',
  color: '#000',
});

const MainTabInReportRoute = {
  ['registrations']: 1,
  ['orders']: 2,
  ['payments']: 3,
  ['ads']: 4,
  ['webinars']: 5,
};

const getRouteByReportId = (reportId: number) => {
  switch (reportId) {
    case 1:
      return routeNames.home;
    case 2:
      return routeNames.orderReports;
    case 3:
      return routeNames.paymentReports;
    case 4:
      return routeNames.adReports;
    case 5:
      return routeNames.webinarReports;
    default:
      return routeNames.home;
  }
};

export const KEY_WEBINAR_QUALITY = 'webinarQuality';
const webinarQuality: ItemTypee = {
  id: KEY_WEBINAR_QUALITY,
  hash: KEY_WEBINAR_QUALITY + 'hash',
  name: 'Качество вебинаров',
  report_id: 5,
  type: '3',
  access_id: 3,
  is_favorite: false,
  user_id: -1,
  static: true,
};
export const KEY_COHGORT = 'cohgort';
const cohgort: ItemTypee = {
  id: KEY_COHGORT,
  hash: KEY_COHGORT + 'hash',
  name: 'Когортный анализ',
  report_id: 3,
  type: '3',
  access_id: 3,
  is_favorite: false,
  user_id: -1,
  static: true,
};

const TabContent: React.FC<TabContentProps> = ({
  mainTab,
  subTab,
  activeContentTab,
  onContentTabChange,
  data,
  onItemClick,
  activeTab,
  tags,
  collapsed,
}) => {
  const navigate = useNavigate();
  const [ searchParams, setSearchParams ] = useSearchParams();
  
  const [ hoveredItem, setHoveredItem ] = useState<number | null>(null);
  const [ searchQuery, setSearchQuery ] = useState('');
  const [ sortOrder, setSortOrder ] = useState<'asc' | 'desc'>('asc');

  const loadingReport = useTypedSelector(state => state.reports.isLoadingReports);
  const savedReports = useTypedSelector(state => state.reports.savedReports);
  const savedDashboards = useTypedSelector(state => state.reports.savedDashboards);
  const userAccess = useTypedSelector(state => state.currentSchool.userAccess);
  const role = useTypedSelector(state => state.currentUser.role);

  const {
    setCurrentDashboard,
    setCurrentReport,
    setMetaCompare,
    setMetrics,
    setGroupings,
    setFilters,
    setFiltersTable,
    setSortTable,
    setCurrentFunnel,
    setCurrentSegment,
    setMetricsChecked,
    setTableClearState,
    setChartClearState
  } = useActions();

  const isReport = mainTab === 'reports';

  const tabButtons = [
    { key: 'all', label: 'Все' },
    { key: 'my', label: 'Мои' },
    { key: 'schools', label: 'Школы' },
    { key: 'scan', label: 'SCAN' },
  ];

  const handleTabChange = (activeKey: string) => {
    onContentTabChange(activeKey as 'all' | 'my' | 'schools' | 'scan');
  };

  const handleSort = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const currentData = isReport ? [ ...savedReports, webinarQuality, cohgort ] : savedDashboards;
  const currentItemHash =  new URLSearchParams(location.search).get('report_id');

  const isWebinarQuality = window.location.pathname.includes(routeNames.reportWebinarQuality);
  const isCohort = window.location.pathname.includes(routeNames.reportCohort);

  const getFilteredData = (contentTab: ContentTab) => {
    return currentData
      .filter((item) =>
        filterByParams(
          item,
          activeTab,
          subTab,
          mainTab,
          tags,
          contentTab,
          searchQuery
        )
      )
      .sort((a, b) =>
        sortOrder === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name)
      );
  };

  const handleItemClick = (item: ItemTypee) => {

    if (item.id === KEY_WEBINAR_QUALITY) {
      navigate(routeNames.reportWebinarQuality);
      if (onItemClick) onItemClick();
      return;
    }

    if (item.id === KEY_COHGORT) {
      navigate(routeNames.reportCohort);
      if (onItemClick) onItemClick();
      return;
    }

    const report = data.find((rep: any) => rep.id === item.id);
    if (!report) return;
    navigate(`${getRouteByReportId(item.report_id)}?report_id=${item.hash}`, {
      state: { report_id: item.hash },
    });

    onItemClick && onItemClick();
  };

  const activateReport = async (item: ItemTypee) => {
    const id = item.id.toString();
    sessionStorage.setItem('dashboard_id', id);
    sessionStorage.setItem('dashboard_hash', item.hash);
    const path = getLinkPathMenu(routeNames.dashboards, userAccess, role);
    navigate(`${path}?report_id=${item.hash}`);

    if (onItemClick) {
      onItemClick();
    }
  };

  const handleCreateReport = () => {
      setMetaCompare({ value: false });
      setMetrics({ data: defaultMetrics });
      setGroupings({ data: defaultGroupings });
      setFilters([]);
      setFiltersTable([]);
      setSortTable({});
      setCurrentFunnel([]);
      setCurrentSegment(null);
      setMetricsChecked({ data: [] });
      setSearchParams({});
      setTableClearState();
      setChartClearState();
    if(mainTab === 'reports'){
      setCurrentReport({});
      navigate(getRouteByReportId(MainTabInReportRoute?.[activeTab as keyof typeof MainTabInReportRoute]));
      onItemClick && onItemClick();
      return;
    }
    if(mainTab === 'dashboards'){
      setCurrentDashboard({});
      navigate(routeNames.dashboards);
      onItemClick && onItemClick();
      return;
    }
  };

  return (
    <div
      className={`${styles['tab-content']} ${
        collapsed ? styles['tab-content-collapsed'] : ''
      }`}
      style={{ width: 600 }}
    >
      {(
        <div>
          <div className={styles['tab-buttons']}>
              <Button
                key={'create'}
                type={'primary'}
                onClick={handleCreateReport}
                style={{
                  width: '120px',
                  height: '35px',
                  borderRadius: '15px',
                  fontWeight: '400',
                  background: '#FF0000',
                  color: '#FFF',
                  borderColor: '#FFF',
                  marginRight: 48
                }}
              >
                {'Создать'}
              </Button>
            {tabButtons.map(({ key, label }) => (
              <Button
                key={key}
                type={activeContentTab === key ? 'primary' : 'default'}
                onClick={() => handleTabChange(key)}
                style={{
                  width: '90px',
                  borderRadius: '15px',
                  fontWeight: activeContentTab === key ? '600' : '400',
                  background: activeContentTab === key ? '#1890ff' : '#fff',
                  color:
                    activeContentTab === key ? '#fff' : 'rgba(0, 0, 0, 0.85)',
                  borderColor: activeContentTab === key ? '#1890ff' : '#d9d9d9',
                }}
              >
                {label}
              </Button>
            ))}
          </div>
          <div>
            <div style={{ display: 'flex', gap: '8px', paddingRight: '15px' }}>
              <Input
                placeholder="Поиск..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  marginBottom: '16px',
                  borderRadius: '5px',
                  height: '43px',
                }}
              />
              <Button
                onClick={handleSort}
                icon={
                  sortOrder === 'asc' ? (
                    <SortDescendingOutlined style={{ fontSize: '30px' }} />
                  ) : (
                    <SortAscendingOutlined style={{ fontSize: '30px' }} />
                  )
                }
                style={{
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  boxShadow: 'none',
                  background: 'none',
                }}
              />
            </div>
            <Loader loading={loadingReport} hasTip>
              {getFilteredData(activeContentTab).map((item) => {

                const isSelect = currentItemHash === item.hash 
                  || (isWebinarQuality && item.id === KEY_WEBINAR_QUALITY) 
                  || (isCohort && item.id === KEY_COHGORT);

                return (
                  <div
                    key={item.id}
                    style={getLinkStyle(hoveredItem === item.id, isSelect)}
                    onMouseEnter={() => setHoveredItem(item.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <a
                      style={{ flexBasis: '100%', color: 'black' }}
                      href={`${location.pathname}?report_id=${item.hash}`}
                      onClick={(event) => {
                        event.preventDefault();
                        if (mainTab === 'reports') {
                          handleItemClick(item);
                        } else if (mainTab === 'dashboards') {
                          activateReport(item);
                        }
                      }}
                    >
                      {item.name} {item.is_favorite && '★'}
                    </a>
                    <div onClick={(e) => e.stopPropagation()}>
                    <ReportMenu
                      item={item}
                      isDashboard={mainTab === 'dashboards'}
                    />
                    </div>
                  </div>
                );
              })}
            </Loader>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabContent;
